// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';

export interface FilterPreset {
  id: string;
  name: string;
  quickFilter?: string;
  dateRange?: { start: Date | null; end: Date | null };
  searchRules?: any[];
  searchOperator?: 'AND' | 'OR';
  createdAt: string;
}

/**
 * Hook to manage saved filter presets
 */
export function useFilterPresets(tableId: string) {
  const [presets, setPresets] = useState<FilterPreset[]>([]);

  // Load presets from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(`filter-presets-${tableId}`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setPresets(parsed);
      } catch (e) {
        console.error('Failed to parse saved filter presets:', e);
      }
    }
  }, [tableId]);

  // Save presets to localStorage
  const savePresets = useCallback(
    (newPresets: FilterPreset[]) => {
      setPresets(newPresets);
      localStorage.setItem(`filter-presets-${tableId}`, JSON.stringify(newPresets));
    },
    [tableId]
  );

  const savePreset = useCallback(
    (preset: Omit<FilterPreset, 'id' | 'createdAt'>) => {
      const newPreset: FilterPreset = {
        ...preset,
        id: `preset-${Date.now()}`,
        createdAt: new Date().toISOString(),
      };
      const updated = [...presets, newPreset];
      savePresets(updated);
      return newPreset.id;
    },
    [presets, savePresets]
  );

  const deletePreset = useCallback(
    (id: string) => {
      const updated = presets.filter((p) => p.id !== id);
      savePresets(updated);
    },
    [presets, savePresets]
  );

  const updatePreset = useCallback(
    (id: string, updates: Partial<FilterPreset>) => {
      const updated = presets.map((p) => (p.id === id ? { ...p, ...updates } : p));
      savePresets(updated);
    },
    [presets, savePresets]
  );

  return {
    presets,
    savePreset,
    deletePreset,
    updatePreset,
  };
}

